import { normalizePublicPath } from "@lib/util/urls"

export type SiteMenuItem = {
  label: string
  url: string
  linkType?: string
  collectionId?: string
  collectionHandle?: string
  categoryId?: string
  categoryHandle?: string
  openInNewTab?: boolean
  children?: SiteMenuItem[]
}

export type SiteMenu = {
  handle: string
  title: string
  items: SiteMenuItem[]
}

export type SiteHeroSlide = {
  eyebrow?: string
  title?: string
  subtitle?: string
  imageUrl?: string
  mobileImageUrl?: string
  imageAlt?: string
  buttonText?: string
  buttonLink?: string
  secondaryButtonText?: string
  secondaryButtonLink?: string
  align?: "left" | "center" | "right"
}

export type SiteTierCard = {
  name?: string
  tagline?: string
  imageUrl?: string
  link?: string
  priceFromText?: string
}

export type SiteCategoryItem = {
  title?: string
  subtitle?: string
  imageUrl?: string
  link?: string
}

export type SiteTrustItem = {
  label?: string
  icon?: string
}

export type SitePageBlock = {
  type: string
  enabled?: boolean
  title?: string
  subtitle?: string
  content?: string
  imageUrl?: string
  mobileImageUrl?: string
  imageAlt?: string
  buttonText?: string
  buttonLink?: string
  secondaryButtonText?: string
  secondaryButtonLink?: string
  eyebrow?: string
  layout?: string
  id?: string
  collectionHandle?: string
  collectionHandles?: string[]
  categoryHandle?: string
  productHandle?: string
  productHandles?: string[]
  priceText?: string
  items?: any[]
  limit?: number
  // hero_slider
  slides?: SiteHeroSlide[]
  autoplayMs?: number
  // tier_showcase
  tiers?: SiteTierCard[]
  showFromPrice?: boolean
  // split_banner
  imagePosition?: "left" | "right"
  background?: "cream" | "ink" | "white"
  // trust_store_strip
  trustItems?: SiteTrustItem[]
  storeCount?: number
  storeCtaText?: string
  storeCtaLink?: string
  showHqLine?: boolean
  // category_strip
  shape?: "circle" | "square" | "portrait"
  [key: string]: any
}

export type SiteMediaItem = {
  id?: string
  name?: string
  url: string
  alt?: string
  type?: string
  width?: string | number
  height?: string | number
  uploadedAt?: string
  source?: string
}

export type SitePage = {
  slug: string
  title: string
  status: "draft" | "published"
  seoTitle?: string
  seoDescription?: string
  blocks?: SitePageBlock[]
}

export type SiteCollectionPage = {
  collectionHandle: string
  enabled?: boolean
  title?: string
  description?: string
  heroImageUrl?: string
  mobileHeroImageUrl?: string
  seoTitle?: string
  seoDescription?: string
  showProductGrid?: boolean
  topBlocks?: SitePageBlock[]
  bottomBlocks?: SitePageBlock[]
}

export type SiteReturnExchangeSection = {
  title: string
  content: string
}

export type SiteReturnExchange = {
  enabled?: boolean
  title?: string
  highlights?: string[]
  intro?: string
  sections?: SiteReturnExchangeSection[]
  contactPhone?: string
  contactEmail?: string
  policyLabel?: string
  policyHref?: string
}

export type SiteCareTemplate = {
  id: string
  name: string
  content: string
}

export type SiteStoreLocation = {
  id: string
  name: string
  type?: "retail" | "corporate" | string
  address: string
  hours: string
  phone: string
  lat: number
  lng: number
  /** Optional saved Google Maps directions link; overrides the lat/lng URL. */
  directionsUrl?: string
  enabled?: boolean
}

export type SiteStoreLocator = {
  title?: string
  subtitle?: string
  markerLogoUrl?: string
  locations: SiteStoreLocation[]
}

export type SiteConfig = {
  _meta?: {
    version?: number
    publishedAt?: string
    updatedAt?: string
  }
  mediaLibrary: SiteMediaItem[]
  branding: {
    brandName: string
    shortName?: string
    seoName?: string
    storeName: string
    logoUrl?: string
    logoDarkUrl?: string
    faviconUrl?: string
    primaryColor?: string
    secondaryColor?: string
    accentColor?: string
    defaultSeoTitle?: string
    defaultSeoDescription?: string
    contactEmail?: string
    contactPhone?: string
    whatsapp?: string
    address?: string
    socialLinks?: Record<string, string>
  }
  header: {
    announcementEnabled?: boolean
    announcementText?: string
    announcementLink?: string
    topBarEnabled?: boolean
    topBarText?: string
    topBarLinks?: { label: string; url: string }[]
    stickyHeader?: boolean
    headerStyle?: string
    showSearch?: boolean
    showAccount?: boolean
    showCart?: boolean
    mainMenuHandle?: string
    mobileMenuHandle?: string
    popularSearches?: string[]
    megaMenuEnabled?: boolean
    ctaButtonText?: string
    ctaButtonLink?: string
    mobileBottomNav?: {
      icon: string
      label: string
      url: string
      enabled?: boolean
    }[]
  }
  footer: {
    footerLogoUrl?: string
    description?: string
    newsletterEnabled?: boolean
    newsletterTitle?: string
    newsletterText?: string
    copyrightText?: string
    footerMenuHandle?: string
    legalMenuHandle?: string
    paymentIcons?: string[]
    contactInfo?: {
      phone?: string
      email?: string
    }
    storeLocations?: string[]
    columns?: {
      id?: string
      heading?: string
      type?: "links" | "newsletter" | "social" | "contact" | "text"
      enabled?: boolean
      width?: "wide" | "auto" | "narrow"
      items?: { label: string; url?: string }[]
      text?: string
    }[]
  }
  theme: {
    colors?: {
      primary?: string
      secondary?: string
      accent?: string
      background?: string
      text?: string
    }
    layout?: {
      containerWidth?: string
      sectionSpacing?: string
    }
    buttons?: {
      style?: string
      radius?: string
      uppercase?: boolean
    }
    productCards?: {
      imageRatio?: string
      showQuickAdd?: boolean
      showBadges?: boolean
    }
  }
  commerce: {
    floatingWhatsapp?: {
      enabled?: boolean
      label?: string
    }
    trustBadges?: string[]
    saleBanner?: {
      enabled?: boolean
      text?: string
      link?: string
    }
    freeShippingThreshold?: string
    /** Max order total (BDT) payable by Cash on Delivery. "" / "0" = no limit. */
    codMaxAmount?: string
    productPage?: {
      defaultSize?: string
      imageRatio?: string
      returnExchange?: SiteReturnExchange
    }
    careTemplates?: SiteCareTemplate[]
  }
  storeLocator?: SiteStoreLocator
  menus: SiteMenu[]
  pages: SitePage[]
  collectionPages: SiteCollectionPage[]
}

const BRAND_NAME = process.env.NEXT_PUBLIC_BRAND_NAME || "Store"
const BRAND_SHORT_NAME = process.env.NEXT_PUBLIC_BRAND_SHORT_NAME || BRAND_NAME
const BRAND_SEO_NAME = process.env.NEXT_PUBLIC_BRAND_SEO_NAME || BRAND_NAME
const BRAND_SUPPORT_EMAIL = process.env.NEXT_PUBLIC_BRAND_SUPPORT_EMAIL || ""
const BRAND_SUPPORT_PHONE = process.env.NEXT_PUBLIC_BRAND_SUPPORT_PHONE || ""
const BRAND_WHATSAPP =
  process.env.NEXT_PUBLIC_BRAND_WHATSAPP || BRAND_SUPPORT_PHONE
const IMAGE_CDN_ORIGIN = process.env.NEXT_PUBLIC_IMAGE_CDN_ORIGIN || ""

const DEFAULT_RETURN_EXCHANGE: SiteReturnExchange = {
  enabled: true,
  title: "Easy Returns & Exchange",
  highlights: [
    "Tell us within 7 days",
    "Free return shipping*",
    "Instant refund on receipt",
  ],
  intro:
    "Your satisfaction is our priority. If something is not right with your order, returning or exchanging it is simple.",
  sections: [
    {
      title: "Return Window",
      content:
        "Request a return or exchange within 7 days of receiving your order.",
    },
    {
      title: "Free Return Shipping",
      content:
        "We cover return shipping for defective products, size or color mismatch, print issues, or wrong item sent.",
    },
    {
      title: "How to Return",
      content:
        "Contact our support team with your order details. Items must be unused, unwashed, and in original packaging. We will arrange pickup for eligible returns.",
    },
    {
      title: "Refunds",
      content:
        "Once we receive your return, refunds are processed quickly to your original payment method.",
    },
    {
      title: "Our Promise",
      content:
        "We stand behind our products and will help resolve rare issues with care.",
    },
  ],
  contactPhone: BRAND_SUPPORT_PHONE,
  contactEmail: BRAND_SUPPORT_EMAIL,
  policyLabel: "View Full Return & Refund Policy",
  policyHref: "/pages/return-policy",
}

const DEFAULT_CARE_TEMPLATES: SiteCareTemplate[] = [
  {
    id: "panjabi-standard",
    name: "Panjabi Standard Care",
    content:
      "Dry clean recommended. Steam or iron on low heat inside out. Store hanging in a cool, dry place.",
  },
]

export const DEFAULT_STORE_LOCATOR: SiteStoreLocator = {
  title: "Store Locator",
  subtitle:
    "Select an outlet to view its location on the map, hours, phone number and directions.",
  markerLogoUrl: "",
  locations: [
    {
      id: "banani",
      name: "Banani Outlet",
      type: "retail",
      address:
        "Alisons Tower (1st Floor), Road 11, Block E, Banani, Dhaka 1213",
      hours: "11.00 AM - 08.00 PM",
      phone: "+88 01844244192, 09638224455",
      lat: 23.793252,
      lng: 90.405979,
      enabled: true,
    },
    {
      id: "bashundhara-city",
      name: "Bashundhara City Outlet",
      type: "retail",
      address:
        "Bashundhara City Shopping Complex, Level 3, Block C, Shop 12-14, Panthapath, Dhaka 1215",
      hours: "10.00 AM - 09.00 PM",
      phone: "+88 01844244190, 09638224455",
      lat: 23.751587,
      lng: 90.390662,
      enabled: true,
    },
    {
      id: "jamuna-future-park",
      name: "Jamuna Future Park Outlet",
      type: "retail",
      address:
        "Shop No: GD018, Ground Floor, East Court, Jamuna Future Park, Dhaka 1229",
      hours: "11.00 AM - 08.00 PM (Close: Wednesday)",
      phone: "+88 01844244189, 09638224455",
      lat: 23.813865,
      lng: 90.424293,
      enabled: true,
    },
    {
      id: "mirpur",
      name: "Mirpur Outlet",
      type: "retail",
      address:
        "Shop: E/1, Level: 2, Rupayan Latifa Shamsuddin Square, Mirpur 1, Dhaka 1216",
      hours: "11.00 AM - 08.00 PM",
      phone: "+88 01844244191, 09638224455",
      lat: 23.8001039,
      lng: 90.3521035,
      enabled: true,
    },
    {
      id: "corporate-headquarters",
      name: "Corporate Headquarters",
      type: "corporate",
      address:
        "Siaam Tower, L-12, Plot-15, Sector-3, Jashimuddin Road, Uttara, Dhaka-1230, Bangladesh",
      hours: "Corporate Office: 10.00 AM - 06.00 PM",
      phone: "09638 22 44 55",
      lat: 23.866,
      lng: 90.3951,
      enabled: true,
    },
  ],
}

const CDN = IMAGE_CDN_ORIGIN

const HERO_SUPERIOR_IMAGE = `${CDN}/262005-1-01KRZPQQ748GR5S1Z2RGHCHGEP.webp`
const HERO_SPECIAL_IMAGE = `${CDN}/253026-3-01KRZPQ0P7YCGPA0B66WXNC426.webp`
const HERO_ELITE_IMAGE = `${CDN}/240078-1-01KRZPJ7YJ1BYRND5PHT8ZYAKN.webp`
const CAT_EID = `${CDN}/263001-1-01KRZPQHTPHHJ7Y1EH2XMV5YC1.webp`
const CAT_PANJABI = `${CDN}/242003-1-01KRZPCY0P18PGCZ0E7CXP62JN.webp`
const CAT_COMBO = `${CDN}/S24001-01KRZPGBNF0AG0YRGS7EDPDT3C.webp`
const CAT_KIDS = `${CDN}/262005K-1-01KRZPQWJCWJDTEAK4JRA3P7RH.webp`
const CAT_WAISTCOAT = `${CDN}/248519-1-01KRZPG24MMNYN07NP8J4J6NTW.webp`
const CAT_TROUSER = `${CDN}/240079-1-1-01KRZPFPN15DCBZM4BQAKX8NC3.webp`
const CAT_PERFUME = `${CDN}/259001-01KRZPMZ848YAN850SZ0VQ1AP1.webp`
const TIER_STANDARD = `${CDN}/240028-1-01KRZPJF202DGKN7GAT8H25B69.webp`
const TIER_SPECIAL = `${CDN}/243011-1-01KRZPFFBNMYCQV3ZEG0413KFC.webp`
const TIER_SUPERIOR = `${CDN}/252004-1-01KRZPPQZB0RXR6NX2JR2GHRGC.webp`
const TIER_ELITE = `${CDN}/240078-1-01KRZPJ7YJ1BYRND5PHT8ZYAKN.webp`
const SPLIT_EID_IMAGE = `${CDN}/253059-1-01KRZPQBW2BNWHQZQZDFVSEJZ8.webp`
const ARTISAN_BG_IMAGE = `${CDN}/252006-1-01KRZPPGFYFWR12PGBT91HN8H8.webp`

export const DEFAULT_SITE_CONFIG: SiteConfig = {
  _meta: {
    version: 10,
    publishedAt: "",
    updatedAt: "",
  },
  mediaLibrary: [],
  branding: {
    brandName: BRAND_NAME,
    shortName: BRAND_SHORT_NAME,
    seoName: BRAND_SEO_NAME,
    storeName: BRAND_NAME,
    logoUrl: "",
    defaultSeoTitle: `${BRAND_SEO_NAME} | Online Store`,
    defaultSeoDescription: `Shop latest arrivals from ${BRAND_NAME}.`,
    contactEmail: BRAND_SUPPORT_EMAIL,
    contactPhone: BRAND_SUPPORT_PHONE,
    whatsapp: BRAND_WHATSAPP,
  },
  header: {
    announcementEnabled: true,
    announcementText: "Premium modest menswear for Eid, Jummah and everyday elegance",
    announcementLink: "",
    topBarEnabled: false,
    topBarText: "",
    topBarLinks: [],
    stickyHeader: true,
    headerStyle: "centered",
    showSearch: true,
    showAccount: true,
    showCart: true,
    mainMenuHandle: "main",
    mobileMenuHandle: "main",
    popularSearches: ["Panjabi", "Waistcoat", "Trouser", "Kids Panjabi", "Eid Collection", "Perfume"],
    megaMenuEnabled: false,
    ctaButtonText: "",
    ctaButtonLink: "",
    mobileBottomNav: [
      { icon: "store", label: "Home", url: "/", enabled: true },
      { icon: "cart", label: "Cart", url: "/cart", enabled: true },
      { icon: "user", label: "Account", url: "/account", enabled: true },
      { icon: "pin", label: "Locate", url: "/location", enabled: true },
    ],
  },
  footer: {
    description: "Premium modest fashion and Islamic wear.",
    newsletterEnabled: true,
    newsletterTitle: "Join our newsletter",
    newsletterText: "Get updates about new arrivals and offers.",
    copyrightText: `© 2026 ${BRAND_NAME}. All rights reserved.`,
    footerMenuHandle: "footer",
    legalMenuHandle: "legal",
    paymentIcons: ["visa", "mastercard", "amex", "sslcommerz"],
    contactInfo: {
      phone: BRAND_SUPPORT_PHONE,
      email: BRAND_SUPPORT_EMAIL,
    },
    storeLocations: [
      "Jamuna Future Park",
      "Banani",
      "Bashundhara City",
      "Mirpur",
    ],
    columns: [],
  },
  theme: {
    colors: {
      primary: "#1A1A1A",
      secondary: "#FFFFFF",
      accent: "#3D5240",
      background: "#FFFFFF",
      text: "#1A1A1A",
    },
    layout: {
      containerWidth: "1440px",
      sectionSpacing: "comfortable",
    },
    buttons: {
      style: "solid",
      radius: "md",
      uppercase: false,
    },
    productCards: {
      imageRatio: "portrait",
      showQuickAdd: true,
      showBadges: true,
    },
  },
  commerce: {
    floatingWhatsapp: {
      enabled: true,
      label: "Chat on WhatsApp",
    },
    trustBadges: ["Secure checkout", "Fast delivery", "Easy exchange"],
    saleBanner: {
      enabled: false,
      text: "",
      link: "/store",
    },
    freeShippingThreshold: "",
    codMaxAmount: "",
    productPage: {
      defaultSize: "M",
      imageRatio: "3:4",
      returnExchange: DEFAULT_RETURN_EXCHANGE,
    },
    careTemplates: DEFAULT_CARE_TEMPLATES,
  },
  storeLocator: DEFAULT_STORE_LOCATOR,
  menus: [
    {
      handle: "main",
      title: "Main Menu",
      items: [
        { label: "Home", url: "/", linkType: "custom", children: [] },
        {
          label: "Panjabi",
          url: "/categories/panjabi",
          linkType: "category",
          categoryHandle: "panjabi",
          children: [
            { label: "Standard Panjabi", url: "/categories/standard-panjabi", linkType: "category", categoryHandle: "standard-panjabi", children: [] },
            { label: "Special Panjabi", url: "/categories/special-panjabi", linkType: "category", categoryHandle: "special-panjabi", children: [] },
            { label: "Superior Panjabi", url: "/categories/superior-panjabi", linkType: "category", categoryHandle: "superior-panjabi", children: [] },
            { label: "Elite Panjabi", url: "/categories/elite-panjabi", linkType: "category", categoryHandle: "elite-panjabi", children: [] },
          ],
        },
        { label: "Kids Panjabi", url: "/categories/kids-panjabi", linkType: "category", categoryHandle: "kids-panjabi", children: [] },
        { label: "Waistcoat", url: "/categories/waistcoat", linkType: "category", categoryHandle: "waistcoat", children: [] },
        { label: "Trouser", url: "/categories/trouser", linkType: "category", categoryHandle: "trouser", children: [] },
        { label: "Perfume", url: "/categories/perfume", linkType: "category", categoryHandle: "perfume", children: [] },
      ],
    },
    {
      handle: "footer",
      title: "Client Services",
      items: [
        { label: "Store Locator", url: "/location", linkType: "custom", children: [] },
        { label: "Shipping Policy", url: "/shipping-policy", linkType: "custom", children: [] },
        { label: "Exchange & Refund Policy", url: "/exchange-refund-policy", linkType: "custom", children: [] },
        { label: "Payment Policy", url: "/payment-policy", linkType: "custom", children: [] },
      ],
    },
    {
      handle: "legal",
      title: "Legal Menu",
      items: [
        { label: "Privacy Policy", url: "/privacy-policy", linkType: "custom", children: [] },
        { label: "Terms & Conditions", url: "/terms-conditions", linkType: "custom", children: [] },
      ],
    },
  ],
  pages: [
    {
      slug: "home",
      title: "Homepage",
      status: "published",
      seoTitle: `${BRAND_SEO_NAME} | Online Store`,
      seoDescription: `Shop latest arrivals from ${BRAND_NAME}.`,
      blocks: [
        {
          type: "hero_slider",
          enabled: true,
          autoplayMs: 0,
          slides: [
            {
              eyebrow: BRAND_SEO_NAME,
              title: "Heritage tailored for the present.",
              subtitle: "Premium Panjabi, waistcoat and modest menswear shaped with quiet luxury, warm texture and thoughtful restraint.",
              imageUrl: HERO_SUPERIOR_IMAGE,
              mobileImageUrl: HERO_SUPERIOR_IMAGE,
              imageAlt: `Featured product by ${BRAND_SHORT_NAME}`,
              buttonText: "Explore Collection",
              buttonLink: "/categories/superior-panjabi",
              secondaryButtonText: "Shop Panjabi",
              secondaryButtonLink: "/categories/panjabi",
              align: "left",
            },
            {
              eyebrow: "Eid Edit",
              title: "Dressed for the moments that matter.",
              subtitle: "Hand-finished panjabi paired with structured waistcoats — ready for Eid, Jummah and everyday elegance.",
              imageUrl: HERO_SPECIAL_IMAGE,
              mobileImageUrl: HERO_SPECIAL_IMAGE,
              imageAlt: `${BRAND_SHORT_NAME} festive collection`,
              buttonText: "Shop Eid",
              buttonLink: "/categories/special-panjabi",
              align: "left",
            },
            {
              eyebrow: "Master Panjabi",
              title: "Four signatures. One language.",
              subtitle: "Standard, Special, Superior and Elite — a disciplined ladder of craft and finish.",
              imageUrl: HERO_ELITE_IMAGE,
              mobileImageUrl: HERO_ELITE_IMAGE,
              imageAlt: `${BRAND_SHORT_NAME} ceremonial collection`,
              buttonText: "Discover the Tiers",
              buttonLink: "/categories/elite-panjabi",
              align: "left",
            },
          ],
        },
        {
          type: "category_strip",
          enabled: true,
          eyebrow: "Shop by category",
          title: "Every silhouette, ready to wear",
          shape: "portrait",
          items: [
            { title: "Eid Collection", imageUrl: CAT_EID, link: "/categories/special-panjabi", imageAlt: "Eid panjabi capsule" },
            { title: "Panjabi", imageUrl: CAT_PANJABI, link: "/categories/panjabi", imageAlt: `${BRAND_SHORT_NAME} panjabi collection` },
            { title: "Combo", imageUrl: CAT_COMBO, link: "/collections/panjabi", imageAlt: `${BRAND_SHORT_NAME} combo sets` },
            { title: "Kids Panjabi", imageUrl: CAT_KIDS, link: "/categories/kids-panjabi", imageAlt: `Kids panjabi by ${BRAND_SHORT_NAME}` },
            { title: "Waistcoat", imageUrl: CAT_WAISTCOAT, link: "/categories/waistcoat", imageAlt: `${BRAND_SHORT_NAME} waistcoat layering` },
            { title: "Trouser", imageUrl: CAT_TROUSER, link: "/categories/trouser", imageAlt: `Tailored trousers by ${BRAND_SHORT_NAME}` },
            { title: "Perfume", imageUrl: CAT_PERFUME, link: "/categories/perfume", imageAlt: `${BRAND_SHORT_NAME} perfume` },
          ],
        },
        {
          type: "tier_showcase",
          enabled: true,
          eyebrow: `${BRAND_SHORT_NAME} signatures`,
          title: "Four signatures. One language of menswear.",
          subtitle: "From Standard to Elite — each tier a step of refinement in fabric, finish and tailoring.",
          showFromPrice: false,
          tiers: [
            { name: "Standard", tagline: "Everyday refinement", imageUrl: TIER_STANDARD, link: "/categories/standard-panjabi" },
            { name: "Special", tagline: "Festive comfort", imageUrl: TIER_SPECIAL, link: "/categories/special-panjabi" },
            { name: "Superior", tagline: "Refined sheen", imageUrl: TIER_SUPERIOR, link: "/categories/superior-panjabi" },
            { name: "Elite", tagline: "Ceremonial pinnacle", imageUrl: TIER_ELITE, link: "/categories/elite-panjabi" },
          ],
        },
        {
          type: "product_carousel",
          enabled: true,
          eyebrow: "Now in store",
          title: "Superior Panjabi",
          subtitle: "Refined sheen and considered tailoring — pulled live from the latest drop.",
          collectionHandle: "superior-panjabi",
          buttonText: "Shop Superior",
          limit: 8,
          layout: "carousel",
        },
        {
          type: "split_banner",
          enabled: true,
          eyebrow: "The Eid Edit",
          title: "Dressed for the moments that matter.",
          subtitle: "Hand-finished panjabi paired with structured waistcoats. Build the look from a single, considered drop.",
          imageUrl: SPLIT_EID_IMAGE,
          imageAlt: "Special Panjabi paired with waistcoat",
          imagePosition: "left",
          background: "cream",
          buttonText: "Shop the Edit",
          buttonLink: "/categories/special-panjabi",
        },
        {
          type: "product_carousel",
          enabled: true,
          eyebrow: "Layered formality",
          title: "Elite Waistcoat",
          subtitle: "Structured ceremonial layering, hand-finished in Dhaka.",
          collectionHandle: "elite-waistcoat",
          buttonText: "Shop Waistcoats",
          limit: 8,
          layout: "carousel",
        },
        {
          type: "trust_store_strip",
          enabled: true,
          trustItems: [
            { label: "Secure checkout", icon: "shield" },
            { label: "Fast delivery across Bangladesh", icon: "truck" },
            { label: "7-day easy exchange", icon: "refresh" },
          ],
          storeCtaText: "Find a store near you",
          storeCtaLink: "/location",
          showHqLine: false,
        },
        {
          type: "artisan_story",
          enabled: true,
          imageUrl: ARTISAN_BG_IMAGE,
          quote: "Crafting heritage through disciplined cuts, tactile fabric and details that speak softly.",
          items: [
            { label: "Premium Fabric", icon: "star" },
            { label: "Thoughtful Tailoring", icon: "shield" },
            { label: "4 Outlets across Dhaka", icon: "map" },
          ],
        },
      ],
    },
  ],
  collectionPages: [],
}

const backendUrl =
  process.env.MEDUSA_BACKEND_URL ||
  process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL ||
  "http://127.0.0.1:9000"

const publishableKey =
  process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY ||
  process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_API_KEY ||
  ""

export const SITE_MANAGER_CACHE_TAG = "site-manager"

export async function getSiteManagerConfig(): Promise<SiteConfig> {
  try {
    const response = await fetch(`${backendUrl}/store/site-manager`, {
      next: {
        revalidate: 60,
        tags: [SITE_MANAGER_CACHE_TAG],
      },
      headers: publishableKey
        ? {
            "x-publishable-api-key": publishableKey,
          }
        : {},
    })

    if (!response.ok) {
      console.error(
        "Site Manager API failed:",
        response.status,
        await response.text(),
      )

      return DEFAULT_SITE_CONFIG
    }

    const data = await response.json()
    const sourceConfig = data.config || {}
    const sourceCommerce = sourceConfig.commerce || {}
    const sourceProductPage = sourceCommerce.productPage || {}
    const sourceReturnExchange = sourceProductPage.returnExchange || {}

    return {
      ...DEFAULT_SITE_CONFIG,
      ...sourceConfig,
      mediaLibrary:
        sourceConfig.mediaLibrary || DEFAULT_SITE_CONFIG.mediaLibrary,
      branding: {
        ...DEFAULT_SITE_CONFIG.branding,
        ...(sourceConfig.branding || {}),
      },
      header: {
        ...DEFAULT_SITE_CONFIG.header,
        ...(sourceConfig.header || {}),
      },
      footer: {
        ...DEFAULT_SITE_CONFIG.footer,
        ...(sourceConfig.footer || {}),
      },
      theme: {
        ...DEFAULT_SITE_CONFIG.theme,
        ...(sourceConfig.theme || {}),
        colors: {
          ...DEFAULT_SITE_CONFIG.theme.colors,
          ...(sourceConfig.theme?.colors || {}),
        },
        layout: {
          ...DEFAULT_SITE_CONFIG.theme.layout,
          ...(sourceConfig.theme?.layout || {}),
        },
        buttons: {
          ...DEFAULT_SITE_CONFIG.theme.buttons,
          ...(sourceConfig.theme?.buttons || {}),
        },
        productCards: {
          ...DEFAULT_SITE_CONFIG.theme.productCards,
          ...(sourceConfig.theme?.productCards || {}),
        },
      },
      commerce: {
        ...DEFAULT_SITE_CONFIG.commerce,
        ...sourceCommerce,
        floatingWhatsapp: {
          ...DEFAULT_SITE_CONFIG.commerce.floatingWhatsapp,
          ...(sourceCommerce.floatingWhatsapp || {}),
        },
        saleBanner: {
          ...DEFAULT_SITE_CONFIG.commerce.saleBanner,
          ...(sourceCommerce.saleBanner || {}),
        },
        productPage: {
          ...DEFAULT_SITE_CONFIG.commerce.productPage,
          ...sourceProductPage,
          returnExchange: {
            ...DEFAULT_SITE_CONFIG.commerce.productPage?.returnExchange,
            ...sourceReturnExchange,
            highlights: Array.isArray(sourceReturnExchange.highlights)
              ? sourceReturnExchange.highlights
              : DEFAULT_SITE_CONFIG.commerce.productPage?.returnExchange
                  ?.highlights,
            sections: Array.isArray(sourceReturnExchange.sections)
              ? sourceReturnExchange.sections
              : DEFAULT_SITE_CONFIG.commerce.productPage?.returnExchange
                  ?.sections,
          },
        },
        careTemplates: Array.isArray(sourceCommerce.careTemplates)
          ? sourceCommerce.careTemplates
          : DEFAULT_SITE_CONFIG.commerce.careTemplates,
      },
      storeLocator: {
        ...DEFAULT_SITE_CONFIG.storeLocator,
        ...(sourceConfig.storeLocator || {}),
        locations: Array.isArray(sourceConfig.storeLocator?.locations)
          ? sourceConfig.storeLocator.locations
          : DEFAULT_SITE_CONFIG.storeLocator?.locations || [],
      },
      menus: sourceConfig.menus || DEFAULT_SITE_CONFIG.menus,
      pages: sourceConfig.pages || DEFAULT_SITE_CONFIG.pages,
      collectionPages:
        sourceConfig.collectionPages || DEFAULT_SITE_CONFIG.collectionPages,
    }
  } catch (error) {
    console.error("Site Manager API error:", error)
    return DEFAULT_SITE_CONFIG
  }
}

export async function fetchStorefrontConfig(): Promise<SiteConfig> {
  return getSiteManagerConfig()
}

export function getMenu(config: SiteConfig, handle?: string) {
  return config.menus?.find((menu) => menu.handle === handle)
}

/**
 * Walk the main header menu and return the path (root → matched leaf) of menu
 * items that lead to a given collection. Medusa has no collection hierarchy, so
 * the breadcrumb on a collection page is reconstructed from the navigation the
 * team curates in Site Manager (e.g. Panjabi → Standard Panjabi). Matches by
 * collectionHandle first, then by a `/collections/<handle>` or
 * `/categories/<handle>` URL. Returns null when the collection isn't in the menu.
 */
export function getCollectionMenuTrail(
  config: SiteConfig,
  collectionHandle: string
): SiteMenuItem[] | null {
  if (!collectionHandle) return null
  const mainHandle = config.header?.mainMenuHandle || "main"
  const menu =
    config.menus?.find((m) => m.handle === mainHandle) || config.menus?.[0]
  if (!menu) return null

  const matches = (item: SiteMenuItem): boolean => {
    if (item.collectionHandle && item.collectionHandle === collectionHandle)
      return true
    const url = item.url || ""
    return (
      url === `/collections/${collectionHandle}` ||
      url === `/categories/${collectionHandle}` ||
      url.endsWith(`/${collectionHandle}`)
    )
  }

  const dfs = (items: SiteMenuItem[], trail: SiteMenuItem[]): SiteMenuItem[] | null => {
    for (const item of items || []) {
      const nextTrail = [...trail, item]
      if (matches(item)) return nextTrail
      if (item.children?.length) {
        const found = dfs(item.children, nextTrail)
        if (found) return found
      }
    }
    return null
  }

  return dfs(menu.items, [])
}

export function getPage(config: SiteConfig, slug: string) {
  return config.pages?.find(
    (page) => page.slug === slug && page.status === "published",
  )
}

export function normalizeStorefrontHref(url?: string) {
  if (!url) {
    return "/"
  }

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url
  }

  return normalizePublicPath(url)
}

export function getCollectionPage(config: SiteConfig, handle: string) {
  return config.collectionPages?.find(
    (page) => page.collectionHandle === handle && page.enabled !== false,
  )
}

export function getMediaItem(config: SiteConfig, idOrUrl: string) {
  return config.mediaLibrary?.find(
    (item) =>
      item.id === idOrUrl || item.url === idOrUrl || item.name === idOrUrl,
  )
}

export function getHomePageOgImage(config: SiteConfig): string | undefined {
  const page = getPage(config, "home")
  const blocks = page?.blocks || []

  const slider = blocks.find((b) => b.type === "hero_slider")
  if (slider?.slides?.[0]?.imageUrl) return slider.slides[0].imageUrl

  const lookbook = blocks.find(
    (b) => b.type === "lookbook_hero" || b.type === "hero",
  )
  if (lookbook?.imageUrl) return lookbook.imageUrl

  const branding = config.branding as { logoUrl?: string } | undefined
  return branding?.logoUrl?.trim() || undefined
}
